import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  AttachMoney,
  People,
  Event,
  Star,
  Schedule,
  Category,
  Store,
  Business,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { analyticsAPI } from '../../services/api';
import { useNotification } from '../../context/NotificationContext';

const mockRevenueData = [
  { month: 'Jan', revenue: 4200, bookings: 85 },
  { month: 'Feb', revenue: 3800, bookings: 76 },
  { month: 'Mar', revenue: 5100, bookings: 102 },
  { month: 'Apr', revenue: 4700, bookings: 94 },
  { month: 'May', revenue: 5800, bookings: 116 },
  { month: 'Jun', revenue: 6200, bookings: 124 },
  { month: 'Jul', revenue: 7100, bookings: 142 },
  { month: 'Aug', revenue: 6800, bookings: 136 },
  { month: 'Sep', revenue: 5900, bookings: 118 },
  { month: 'Oct', revenue: 6400, bookings: 128 },
  { month: 'Nov', revenue: 7200, bookings: 144 },
  { month: 'Dec', revenue: 8100, bookings: 162 },
];

const mockServiceData = [
  { name: 'Hair Cut & Style', bookings: 245, revenue: 11025, color: '#8884d8' },
  { name: 'Hair Coloring', bookings: 156, revenue: 13260, color: '#82ca9d' },
  { name: 'Manicure', bookings: 189, revenue: 4725, color: '#ffc658' },
  { name: 'Pedicure', bookings: 134, revenue: 4690, color: '#ff7300' },
  { name: 'Facial Treatment', bookings: 98, revenue: 6370, color: '#00ff88' },
];

const mockHourlyData = [
  { hour: '9 AM', bookings: 12 },
  { hour: '10 AM', bookings: 18 },
  { hour: '11 AM', bookings: 25 },
  { hour: '12 PM', bookings: 22 },
  { hour: '1 PM', bookings: 15 },
  { hour: '2 PM', bookings: 28 },
  { hour: '3 PM', bookings: 32 },
  { hour: '4 PM', bookings: 29 },
  { hour: '5 PM', bookings: 24 },
  { hour: '6 PM', bookings: 19 },
  { hour: '7 PM', bookings: 14 },
  { hour: '8 PM', bookings: 8 },
];

const mockTopCustomers = [
  { id: 1, name: 'Sarah Johnson', bookings: 24, revenue: 1080, avatar: 'SJ' },
  { id: 2, name: 'Emily Davis', bookings: 18, revenue: 945, avatar: 'ED' },
  { id: 3, name: 'Jessica Wilson', bookings: 16, revenue: 720, avatar: 'JW' },
  { id: 4, name: 'Amanda Brown', bookings: 14, revenue: 630, avatar: 'AB' },
  { id: 5, name: 'Lisa Garcia', bookings: 12, revenue: 780, avatar: 'LG' },
];

const mockTopSalons = [
  { id: 1, name: 'Glamour Studio', bookings: 156, revenue: 7020, rating: 4.8, services: 12 },
  { id: 2, name: 'Beauty Lounge', bookings: 134, revenue: 6230, rating: 4.6, services: 8 },
  { id: 3, name: 'Nail Paradise', bookings: 98, revenue: 2450, rating: 4.9, services: 6 },
  { id: 4, name: 'Spa Serenity', bookings: 87, revenue: 3045, rating: 4.7, services: 10 },
  { id: 5, name: 'Radiance Spa', bookings: 76, revenue: 4940, rating: 4.5, services: 14 },
];

const mockSalonPerformanceData = [
  { name: 'Glamour Studio', bookings: 156, revenue: 7020, rating: 4.8, color: '#8884d8' },
  { name: 'Beauty Lounge', bookings: 134, revenue: 6230, rating: 4.6, color: '#82ca9d' },
  { name: 'Nail Paradise', bookings: 98, revenue: 2450, rating: 4.9, color: '#ffc658' },
  { name: 'Spa Serenity', bookings: 87, revenue: 3045, rating: 4.7, color: '#ff7300' },
  { name: 'Radiance Spa', bookings: 76, revenue: 4940, rating: 4.5, color: '#00ff88' },
];

const Analytics = () => {
  const { showSuccess, showError } = useNotification();
  const [timeRange, setTimeRange] = useState('12months');
  const [loading, setLoading] = useState(false);

  const calculateGrowth = (current, previous) => {
    if (previous === 0) return 0;
    return ((current - previous) / previous * 100).toFixed(1);
  };

  const currentMonthRevenue = mockRevenueData[mockRevenueData.length - 1].revenue;
  const previousMonthRevenue = mockRevenueData[mockRevenueData.length - 2].revenue;
  const revenueGrowth = calculateGrowth(currentMonthRevenue, previousMonthRevenue);

  const currentMonthBookings = mockRevenueData[mockRevenueData.length - 1].bookings;
  const previousMonthBookings = mockRevenueData[mockRevenueData.length - 2].bookings;
  const bookingsGrowth = calculateGrowth(currentMonthBookings, previousMonthBookings);

  const totalRevenue = mockRevenueData.reduce((sum, item) => sum + item.revenue, 0);
  const totalBookings = mockRevenueData.reduce((sum, item) => sum + item.bookings, 0);
  const averageBookingValue = (totalRevenue / totalBookings).toFixed(2);

  const getStringAvatar = (name) => {
    return {
      children: name.split(' ').map(n => n[0]).join(''),
    };
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Analytics Dashboard
        </Typography>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeRange}
            label="Time Range"
            onChange={(e) => setTimeRange(e.target.value)}
          >
            <MenuItem value="7days">Last 7 Days</MenuItem>
            <MenuItem value="30days">Last 30 Days</MenuItem>
            <MenuItem value="3months">Last 3 Months</MenuItem>
            <MenuItem value="6months">Last 6 Months</MenuItem>
            <MenuItem value="12months">Last 12 Months</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Revenue
                  </Typography>
                  <Typography variant="h4">
                    ₹{totalRevenue.toLocaleString()}
                  </Typography>
                  <Box display="flex" alignItems="center" mt={1}>
                    {revenueGrowth >= 0 ? (
                      <TrendingUp color="success" fontSize="small" />
                    ) : (
                      <TrendingDown color="error" fontSize="small" />
                    )}
                    <Typography
                      variant="body2"
                      color={revenueGrowth >= 0 ? 'success.main' : 'error.main'}
                      sx={{ ml: 0.5 }}
                    >
                      {revenueGrowth}% from last month
                    </Typography>
                  </Box>
                </Box>
                <AttachMoney color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Bookings
                  </Typography>
                  <Typography variant="h4">
                    {totalBookings.toLocaleString()}
                  </Typography>
                  <Box display="flex" alignItems="center" mt={1}>
                    {bookingsGrowth >= 0 ? (
                      <TrendingUp color="success" fontSize="small" />
                    ) : (
                      <TrendingDown color="error" fontSize="small" />
                    )}
                    <Typography
                      variant="body2"
                      color={bookingsGrowth >= 0 ? 'success.main' : 'error.main'}
                      sx={{ ml: 0.5 }}
                    >
                      {bookingsGrowth}% from last month
                    </Typography>
                  </Box>
                </Box>
                <Event color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Active Salons
                  </Typography>
                  <Typography variant="h4">
                    {mockTopSalons.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Partner salons
                  </Typography>
                </Box>
                <Store color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Avg. Salon Rating
                  </Typography>
                  <Typography variant="h4">
                    {(mockTopSalons.reduce((sum, salon) => sum + salon.rating, 0) / mockTopSalons.length).toFixed(1)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Overall rating
                  </Typography>
                </Box>
                <Star color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Salon Metrics Row */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Avg. Booking Value
                  </Typography>
                  <Typography variant="h4">
                    ₹{averageBookingValue}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Per appointment
                  </Typography>
                </Box>
                <AttachMoney color="secondary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Active Customers
                  </Typography>
                  <Typography variant="h4">
                    {mockTopCustomers.length * 20}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    This month
                  </Typography>
                </Box>
                <People color="secondary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Services
                  </Typography>
                  <Typography variant="h4">
                    {mockTopSalons.reduce((sum, salon) => sum + salon.services, 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Across all salons
                  </Typography>
                </Box>
                <Category color="secondary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Top Salon Revenue
                  </Typography>
                  <Typography variant="h4">
                    ₹{Math.max(...mockTopSalons.map(s => s.revenue)).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Best performer
                  </Typography>
                </Box>
                <Business color="secondary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Row 1 */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Revenue & Bookings Trend
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={mockRevenueData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="revenue" fill="#8884d8" name="Revenue ($)" />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="bookings"
                    stroke="#82ca9d"
                    strokeWidth={3}
                    name="Bookings"
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Service Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={mockServiceData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="bookings"
                  >
                    {mockServiceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Row 2 */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Salon Performance Comparison
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={mockSalonPerformanceData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis yAxisId="left" />
                  <YAxis yAxisId="right" orientation="right" />
                  <Tooltip />
                  <Legend />
                  <Bar yAxisId="left" dataKey="revenue" fill="#8884d8" name="Revenue ($)" />
                  <Bar yAxisId="right" dataKey="bookings" fill="#82ca9d" name="Bookings" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Performing Salons
              </Typography>
              <Box>
                {mockTopSalons.map((salon, index) => (
                  <Box
                    key={salon.id}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    py={1}
                    borderBottom={index < mockTopSalons.length - 1 ? '1px solid #eee' : 'none'}
                  >
                    <Box display="flex" alignItems="center">
                      <Avatar sx={{ bgcolor: 'primary.main', mr: 2, width: 32, height: 32 }}>
                        <Store fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {salon.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {salon.bookings} bookings • ⭐ {salon.rating}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="body2" fontWeight="bold" color="primary">
                      ₹{salon.revenue.toLocaleString()}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Row 3 */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Peak Hours Analysis
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={mockHourlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hour" />
                  <YAxis />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="bookings"
                    stroke="#8884d8"
                    fill="#8884d8"
                    fillOpacity={0.6}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Customers
              </Typography>
              <Box>
                {mockTopCustomers.map((customer, index) => (
                  <Box
                    key={customer.id}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    mb={2}
                  >
                    <Box display="flex" alignItems="center">
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mr: 2, minWidth: 20 }}
                      >
                        #{index + 1}
                      </Typography>
                      <Avatar
                        {...getStringAvatar(customer.name)}
                        sx={{ mr: 2, width: 32, height: 32, fontSize: 14 }}
                      />
                      <Box>
                        <Typography variant="body2">
                          {customer.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {customer.bookings} bookings
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="body2" fontWeight="bold">
                      ₹{customer.revenue}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Service Performance Table */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Service Performance
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Service</TableCell>
                      <TableCell align="right">Bookings</TableCell>
                      <TableCell align="right">Revenue</TableCell>
                      <TableCell align="right">Avg. Price</TableCell>
                      <TableCell align="right">Performance</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {mockServiceData.map((service) => {
                      const avgPrice = (service.revenue / service.bookings).toFixed(2);
                      const maxBookings = Math.max(...mockServiceData.map(s => s.bookings));
                      const performance = (service.bookings / maxBookings) * 100;
                      
                      return (
                        <TableRow key={service.name}>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: '50%',
                                  backgroundColor: service.color,
                                  mr: 2,
                                }}
                              />
                              {service.name}
                            </Box>
                          </TableCell>
                          <TableCell align="right">{service.bookings}</TableCell>
                          <TableCell align="right">₹{service.revenue.toLocaleString()}</TableCell>
                          <TableCell align="right">₹{avgPrice}</TableCell>
                          <TableCell align="right">
                            <Box display="flex" alignItems="center">
                              <LinearProgress
                                variant="determinate"
                                value={performance}
                                sx={{ width: 60, mr: 1 }}
                              />
                              <Typography variant="body2">
                                {performance.toFixed(0)}%
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Analytics;